import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { Classes, Label, InputGroup } from '@blueprintjs/core';
import cloneDeep from 'lodash.clonedeep';
import Form, { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayout } from 'components/FormLayout';
import Dialog from 'shared/modal/dialog';
import { TargetModel } from 'data/target/target.types';
import { TargetOptions as TargetOptionsModel } from 'data/case/case.types';
import { TargetDomain } from 'data/targetDomain/targetDomain.types';
import { FormTemplate, FormTemplateSection } from 'data/template/template.types';
import { CONFIG_FORM_MODE } from 'shared/popup/config-form-popup';
import setAllTemplateFieldsAsReadOnly from 'utils/formTemplate/setAllTemplateFieldsAsReadOnly';
import { RootState } from 'data/root.reducers';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import TargetOptionsForm from './components/TargetOptionsForm';
import {
  targetOptionsTemplate, i18nKeyPrefix, Ref, targetDomainTemplate, targetOptionsVolteTemplate
} from './TargetOptions.types';
import { getSupportedTargetWithTargetOptions } from './utils/targetOptionsForm.adapter';

const mapState = (state: RootState) => ({
  supportedFeaturesTargets: supportedFeaturesSelectors.getSupportedFeaturesTargets(state)
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & {
  isOpen?: boolean;
  title: string,
  data: TargetOptionsModel,
  message?: string,
  target?: TargetModel,
  shoudConfigPullLocationUpdates: boolean,
  shoudConfigTargetDomainList: boolean,
  shouldConfigTargetOptions: boolean,
  targetDomainList: TargetDomain[],
  onSubmit: (params: TargetOptionsModel) => void;
  onClose: () => void;
  currentStateMode: string;
  allowedTargetsToSetOptions: string[];
}

const TargetOptionsDialog = ({
  data,
  isOpen,
  title,
  message,
  target,
  shoudConfigPullLocationUpdates,
  shoudConfigTargetDomainList,
  targetDomainList,
  shouldConfigTargetOptions,
  onClose,
  onSubmit,
  currentStateMode,
  supportedFeaturesTargets,
  allowedTargetsToSetOptions
}: Props): JSX.Element => {
  const [formData, setFormData] = useState<Partial<TargetOptionsModel>>({});
  const [targetDomainTemplateDef, setTargetDomainTemplateDef] = useState<FormTemplate>(targetDomainTemplate);
  const [targetOptionsTemplates, setTargetOptionsTemplates] = useState<FormTemplate[]>([]);
  const [isLocPollFreqValueValid, setIsLocPollFreqValueValid] = useState<boolean>(true);
  const formRef = useRef<Ref>(null);
  const targetDomainFormRef = useRef<Ref>(null);
  const targetOptionsVolteFormRef = useRef<Ref>(null);
  const targetOptionsFormRef = useRef<Ref>(null);
  const { t } = useTranslation();

  const getTargetOptionsFormTemplate = (
    formTemplate: FormTemplate,
    mode: string,
  ): FormTemplate => {
    if (mode === CONFIG_FORM_MODE.View) {
      formTemplate = cloneDeep(formTemplate);
      if (formTemplate.main?.fields) {
        setAllTemplateFieldsAsReadOnly(formTemplate.main?.fields);
      }
    }
    return formTemplate;
  }

  useEffect(() => {
    if (data != null) {
      setFormData(data);

      const clonedTemplate = cloneDeep(targetDomainTemplateDef);
      const domainValue = data.domain ?? '';
      (clonedTemplate.main as FormTemplateSection).fields.SIP_DOMAIN.initial = domainValue;
      (clonedTemplate.main as FormTemplateSection).fields.SIP_DOMAIN.options = targetDomainList
        .map((item) => ({
          value: item.domain,
          label: item.domain,
        }));

      setTargetDomainTemplateDef(clonedTemplate);
    }
  }, [data]);

  useEffect(() => {
    if (target != null && allowedTargetsToSetOptions.includes(target.primaryType as string)) {
      setTargetOptionsTemplates(getSupportedTargetWithTargetOptions(supportedFeaturesTargets, currentStateMode === CONFIG_FORM_MODE.View));
    }
  }, [supportedFeaturesTargets, target, allowedTargetsToSetOptions]);

  useEffect(() => {
    if (targetDomainList?.length > 0 && currentStateMode !== CONFIG_FORM_MODE.View) {
      const clonedTemplate = cloneDeep(targetDomainTemplateDef);
      (clonedTemplate.main as FormTemplateSection).fields.SIP_DOMAIN.initial = targetDomainList[0].domain;
      (clonedTemplate.main as FormTemplateSection).fields.SIP_DOMAIN.options = targetDomainList
        .map((item) => ({
          value: item.domain,
          label: item.domain,
        }));

      setTargetDomainTemplateDef(clonedTemplate);
    }
  }, [targetDomainList]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (fieldName === 'LOC_POLL_FREQ') {
      const locPollFreqValue = Number(value);
      setIsLocPollFreqValueValid(locPollFreqValue >= 0 && locPollFreqValue <= 99999999 && locPollFreqValue % 30 === 0);
    }

    setFormData((current) => ({
      ...current,
      [fieldName]: value,
    }));
  };

  const handleSubmit = () => {
    const isValid = formRef?.current?.isValid(true, true);
    const isConfigTargetOptionsValid = targetOptionsVolteFormRef?.current?.isValid(true, true);
    const isTargetDomainValid = targetDomainFormRef?.current?.isValid(true, true);

    let values = {
      ...formData,
    };

    if (isValid && shoudConfigPullLocationUpdates) {
      values = {
        ...formData,
        ...formRef?.current?.getValues(),
      };
    }

    if (targetOptionsFormRef?.current?.isValid(true, true)) {
      values = {
        ...formData,
        ...targetOptionsFormRef?.current?.getValues(),
      };
    }

    if (isTargetDomainValid && shoudConfigTargetDomainList) {
      values = {
        ...formData,
        ...targetDomainFormRef?.current?.getValues(),
      };
    }

    if (isConfigTargetOptionsValid && shouldConfigTargetOptions) {
      values = {
        ...formData,
        ...targetOptionsVolteFormRef?.current?.getValues(),
      };
    }

    setFormData(values);
    onSubmit(values);

  };

  const renderForm = () => {
    return (
      <div className={Classes.DIALOG_BODY}>
        {message && (
          <p>{message}</p>
        )}
        {
          target && (
            <Label>
              {target.primaryType}
              <InputGroup
                data-test="TargetOptionsFormValue"
                autoComplete="off"
                autoCorrect="off"
                className="input-maxwidth"
                type="text"
                disabled
                value={target.primaryValue}
              />
            </Label>
          )
        }
        <div>
          {shoudConfigPullLocationUpdates && ( // Global pool location frequency target option
            <TargetOptionsForm
              ref={formRef}
              data={formData}
              template={getTargetOptionsFormTemplate(targetOptionsTemplate, currentStateMode)}
              i18nKeyPrefix={i18nKeyPrefix}
              onFieldUpdate={handleFieldUpdate}
            />
          )}
          {shoudConfigTargetDomainList && (
            <TargetOptionsForm
              ref={targetDomainFormRef}
              data={formData}
              template={getTargetOptionsFormTemplate(targetDomainTemplateDef, currentStateMode)}
              i18nKeyPrefix={i18nKeyPrefix}
              onFieldUpdate={handleFieldUpdate}
            />
          )}
          {shouldConfigTargetOptions && (
            <TargetOptionsForm
              ref={targetOptionsVolteFormRef}
              data={formData}
              template={targetOptionsVolteTemplate}
              i18nKeyPrefix={i18nKeyPrefix}
              onFieldUpdate={handleFieldUpdate}
            />
          )}
        </div>
        <div>
          {
            targetOptionsTemplates.map((template) => (
              <FormLayout key={template.key}>
                <Form
                  ref={targetOptionsFormRef as any}
                  name={template.formId ?? template.key}
                  defaults={formData}
                  fields={(template.main as FormTemplateSection).fields}
                  layout={(template.main as FormTemplateSection).metadata.layout?.rows}
                  i18nKeyPrefix={i18nKeyPrefix}
                  fieldUpdateHandler={handleFieldUpdate}
                />
              </FormLayout>
            ))
          }
        </div>
      </div>
    );
  };

  return (
    <div>
      <Dialog
        autoFocus
        isOpen={isOpen}
        title={title}
        className="TargetOptionsDialog"
        customComponent={renderForm}
        onClose={onClose}
        onSubmit={handleSubmit}
        disableSave={!isLocPollFreqValueValid}
        doNotDisplaySaveButton={currentStateMode === 'view'}
        closeText={currentStateMode === 'view' ? t('common.button.close') : t('common.button.cancel')}
      />
    </div>
  );
};

export default connector(TargetOptionsDialog);
